import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { TodoProjectResponse } from '@/lib/api';
import { PROJECT_COLORS, resolveProjectColor } from '../lib/projectColors';

interface ProjectsManagerProps {
  projects: TodoProjectResponse[];
  onCreate: (input: { name: string; color?: string | null }) => Promise<unknown>;
  onUpdate: (
    projectId: string,
    input: { name?: string; color?: string | null },
  ) => Promise<unknown>;
  onDelete: (projectId: string) => Promise<unknown>;
}

/** A dot that opens a palette popover to pick a project's color. */
function ColorDot({
  color,
  onPick,
  ariaLabel,
}: {
  color: string | null;
  onPick: (key: string) => void;
  ariaLabel: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          className={cn(
            'h-4 w-4 flex-shrink-0 rounded-full ring-offset-1 hover:ring-2 hover:ring-ring',
            resolveProjectColor(color).dot,
          )}
        />
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2">
        <div className="grid grid-cols-5 gap-1.5">
          {PROJECT_COLORS.map((c) => (
            <button
              key={c.key}
              type="button"
              aria-label={c.label}
              title={c.label}
              onClick={() => {
                onPick(c.key);
                setOpen(false);
              }}
              className={cn('h-5 w-5 rounded-full hover:ring-2 hover:ring-ring', c.dot)}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Manage the workspace's projects from List Settings: add, rename, recolor,
 * delete. Projects are shared across every list in the workspace; deleting one
 * un-assigns its tasks (it never deletes tasks). Mutations apply immediately.
 */
export function ProjectsManager({ projects, onCreate, onUpdate, onDelete }: ProjectsManagerProps) {
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState<string>(PROJECT_COLORS[0].key);
  const [busy, setBusy] = useState(false);

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await onCreate({ name, color: newColor });
      setNewName('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label>Projects</Label>
      <p className="text-xs text-muted-foreground">
        Shared across this workspace. A task can belong to one project; deleting a project
        un-assigns its tasks (never deletes them).
      </p>

      <div className="space-y-1.5">
        {projects.map((p) => (
          <div key={p.projectId} className="flex items-center gap-2">
            <ColorDot
              color={p.color}
              ariaLabel={`Color for "${p.name}"`}
              onPick={(key) => void onUpdate(p.projectId, { color: key })}
            />
            <Input
              defaultValue={p.name}
              className="h-8 flex-1"
              aria-label={`Rename "${p.name}"`}
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v && v !== p.name) void onUpdate(p.projectId, { name: v });
                else e.target.value = p.name;
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              }}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 flex-shrink-0 text-muted-foreground hover:text-destructive"
              aria-label={`Delete "${p.name}"`}
              onClick={() => void onDelete(p.projectId)}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
        {projects.length === 0 && (
          <p className="text-xs text-muted-foreground/70">No projects yet.</p>
        )}
      </div>

      <div className="flex items-center gap-2 pt-1">
        <ColorDot color={newColor} ariaLabel="New project color" onPick={setNewColor} />
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="New project name…"
          className="h-8 flex-1"
          aria-label="New project name"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void handleAdd();
            }
          }}
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="flex-shrink-0"
          onClick={handleAdd}
          disabled={busy || !newName.trim()}
        >
          <Plus className="mr-1 h-3.5 w-3.5" />
          Add
        </Button>
      </div>
    </div>
  );
}
